import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCheDoTrienKhai } from '../../../hooks/useCheDoTrienKhai';
import { useLoi } from '../../../hooks/useLoi';
import { usePhamVi } from '../../../hooks/usePhamVi';
import { useT } from '../../../i18n';
import { luuTep } from '../../../lib/luu-tep';
import { licenseCucBoRepository } from '../../../lib/repositories/license-cuc-bo.repository';
import { useToast } from '../../../lib/toast';
import { useAuthStore } from '../../../stores/auth';
import { DA_NHAP } from '../constants';
import type { LicenseCucBo, LoaiTepNhap } from '../types';

const KHOA = ['license-cuc-bo'] as const;

/**
 * Người này có thấy màn license cục bộ và băng cảnh báo không: chỉ ở bản
 * on-premise, và chỉ quản trị viên bản cài (Tenant Admin, cổng tenant) — API
 * cục bộ từ chối mọi người khác (`platform.license.runtime.access`), gọi thử
 * chỉ để nhận 403 là thêm một dòng audit từ chối cho mỗi lần tải trang.
 */
export function useDuocXemLicenseCucBo(): boolean {
  const { onPremise } = useCheDoTrienKhai();
  const phamVi = usePhamVi();
  const laQuanTri = useAuthStore((s) => s.user?.roles.includes('vaiTro.quanTriToChuc') ?? false);
  return onPremise && phamVi === 'tenant' && laQuanTri;
}

/**
 * Trạng thái license của bản cài. Màn license và băng cảnh báo dùng chung một
 * truy vấn. Làm mới mỗi 5 phút: `days_left` và trạng thái đổi theo đồng hồ, và
 * License Agent kiểm lại tệp mỗi 15 phút.
 */
export function useLicenseCucBo(batDau = true) {
  return useQuery<LicenseCucBo>({
    queryKey: [...KHOA, 'trang-thai'],
    queryFn: licenseCucBoRepository.trangThai,
    enabled: batDau,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: 1,
  });
}

export function useNhapTepLicense() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    /** `noiDung` là tệp đã chọn, hoặc chuỗi JWS dán vào — gửi nguyên văn làm thân request. */
    mutationFn: (bien: { loai: LoaiTepNhap; noiDung: Blob | string }) =>
      licenseCucBoRepository.nhapTep(
        bien.loai,
        typeof bien.noiDung === 'string' ? new Blob([bien.noiDung], { type: 'text/plain' }) : bien.noiDung,
      ),
    onSuccess: (moi, bien) => {
      qc.setQueryData([...KHOA, 'trang-thai'], moi);
      toast.success(t(DA_NHAP[bien.loai]));
    },
  });
}

export function useTaiYeuCauKichHoat() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: async () => {
      luuTep(await licenseCucBoRepository.taiYeuCauKichHoat(), 'activation-request.json');
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}
